import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search, Users, FileText, BookOpen, Calendar, Mail, Cable, ArrowRight,
} from "lucide-react";

/**
 * CommandBar — Phase 2
 * A ⌘K / Ctrl+K slide-down text-input + option list.
 * Simpler than a full Linear-style K-bar: search filters a small static option
 * list and Enter routes.
 */
const OPTIONS = [
    { key: "hire",     label: "Activate a Workforce Member",   Icon: Users,     to: "/app/workforce" },
    { key: "doc",      label: "Draft a document",         Icon: FileText,  to: "/app/documents" },
    { key: "kb",       label: "Ask your knowledge base",  Icon: BookOpen,  to: "/app/knowledge" },
    { key: "meeting",  label: "Upload a meeting",         Icon: Calendar,  to: "/app/meetings" },
    { key: "email",    label: "Compose an email",         Icon: Mail,      to: "/app/email" },
    { key: "connect",  label: "Connect a tool",           Icon: Cable,     to: "/app/workforce/hired" },
];

export default function CommandBar({ open, onClose }) {
    const [q, setQ] = useState("");
    const [cursor, setCursor] = useState(0);
    const nav = useNavigate();
    const inputRef = useRef(null);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return OPTIONS;
        return OPTIONS.filter((o) => o.label.toLowerCase().includes(term));
    }, [q]);

    useEffect(() => {
        if (open) {
            setQ("");
            setCursor(0);
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        function onKey(e) {
            if (e.key === "Escape") { onClose?.(); return; }
            if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(filtered.length - 1, c + 1)); return; }
            if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); return; }
            if (e.key === "Enter") {
                e.preventDefault();
                const opt = filtered[cursor];
                if (opt) { nav(opt.to); onClose?.(); }
            }
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, filtered, cursor, nav, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[120] bg-black/40 flex items-start justify-center p-4 pt-[10vh]"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="command-bar"
        >
            <div className="w-full max-w-[560px] rounded-2xl bg-white border border-[#e8e8e3] shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 h-12 border-b border-[#e8e8e3]">
                    <Search className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setCursor(0); }}
                        placeholder="Type a command or search…"
                        data-testid="command-bar-input"
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400"
                    />
                    <span className="text-[10px] font-mono text-gray-400 border border-[#e8e8e3] rounded px-1.5 py-0.5">
                        ESC
                    </span>
                </div>
                <ul className="max-h-[50vh] overflow-y-auto py-1" data-testid="command-bar-list">
                    {filtered.length === 0 && (
                        <li className="px-4 py-3 text-sm text-gray-400">No matches.</li>
                    )}
                    {filtered.map(({ key, label, Icon, to }, i) => (
                        <li
                            key={key}
                            data-testid={`command-bar-item-${key}`}
                            onMouseEnter={() => setCursor(i)}
                            onClick={() => { nav(to); onClose?.(); }}
                            className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer text-sm ${
                                i === cursor ? "bg-[#f0fdf4]" : "hover:bg-[#fafaf7]"
                            }`}
                        >
                            <Icon className="w-4 h-4 text-gray-500" strokeWidth={1.75} />
                            <span className="flex-1 text-gray-800">{label}</span>
                            {i === cursor && <ArrowRight className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} />}
                        </li>
                    ))}
                </ul>
                <div className="border-t border-[#e8e8e3] px-4 py-2 flex items-center justify-between text-[10.5px] text-gray-400">
                    <span>↑↓ navigate · ↵ select</span>
                    <span>⌘K to reopen anywhere</span>
                </div>
            </div>
        </div>
    );
}
